import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';
import CustomerJourneyModal from './CustomerJourneyModal';
import SLAAlertBanner from './SLAAlertBanner';
import CarSelector from './CarSelector';

const WORKFLOW_STEPS = [
  'Booking',
  'Document Complete',
  'Loan Login',
  'Verification',
  'Account Opening',
  'Agreement',
  'Down Payment',
  'Disbursement',
  'RTO (Passing)',
  'Delivery'
];

const STATUS_CONFIG: Record<string, { bg: string; text: string; border: string; label: string }> = {
  Pending: { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B', label: 'Pending' },
  Hold:    { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444', label: 'Hold' },
  Done:    { bg: '#D1FAE5', text: '#065F46', border: '#10B981', label: 'Done' },
};

const AdminMasterDashboard: React.FC = () => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [users, setUsers]         = useState<any[]>([]);
  const [bookingCount, setBookingCount] = useState<number>(0);
  const [loading, setLoading]     = useState(true);
  const [activeStep, setActiveStep] = useState<string | null>(null);
  const [search, setSearch]        = useState('');
  const [saving, setSaving]        = useState<string | null>(null);
  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(null);
  const [error, setError]          = useState<string | null>(null);

  // Proper Update Status Modal state
  const [activeUpdateModal, setActiveUpdateModal] = useState<{
    stepId: string;
    stepName: string;
    customerName: string;
    currentStatus: string;
    currentReason?: string;
  } | null>(null);
  const [modalStatus, setModalStatus] = useState<string>('');
  const [modalReason, setModalReason] = useState<string>('');

  // Edit Car Details Modal state
  const [editingCarModal, setEditingCarModal] = useState<{
    id: string;
    customerName: string;
    phone: string;
    currentCar: string;
  } | null>(null);
  const [selectedCarDetails, setSelectedCarDetails] = useState<string>('');
  const [savingCar, setSavingCar] = useState<boolean>(false);

  const userStr = sessionStorage.getItem('aswCrmUser') || sessionStorage.getItem('aswUser') || localStorage.getItem('aswCrmUser') || localStorage.getItem('aswUser');
  const userId  = userStr ? JSON.parse(userStr).id : null;

  const fetchData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const [custRes, userRes] = await Promise.all([
        apiService.getCrmCustomers(),
        apiService.getCrmUsers()
      ]);
      if (custRes.success) {
        const custs = custRes.data || [];
        setCustomers(custs);
        const count = custs.filter((c: any) => {
          const step = (c.crm_workflow_steps || []).find((s: any) => s.step_name === 'Booking');
          return step && step.status === 'Done';
        }).length;
        setBookingCount(count);
      }
      else if (!silent) setError('Failed to load customers. Please try again.');
      if (userRes.success) setUsers(userRes.data);
    } catch {
      if (!silent) setError('Network error — unable to reach the server.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { 
    fetchData(); 
    const timer = setInterval(() => {
      fetchData(true);
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const openUpdateModal = (step: any, customer: any) => {
    setActiveUpdateModal({
      stepId: step.id,
      stepName: step.step_name,
      customerName: customer.customer_name,
      currentStatus: step.status,
      currentReason: step.reason || ''
    });
    setModalStatus(step.status);
    setModalReason(step.reason || '');
  };

  const handleModalSubmit = async () => {
    if (!activeUpdateModal) return;
    const { stepId } = activeUpdateModal;
    setSaving(stepId);

    // Optimistically update local state for instant UI feedback
    setCustomers(prev => {
      const updated = prev.map(cust => ({
        ...cust,
        crm_workflow_steps: (cust.crm_workflow_steps || []).map((s: any) =>
          s.id === stepId ? { ...s, status: modalStatus, reason: modalStatus === 'Done' ? null : modalReason || s.reason } : s
        )
      }));
      const count = updated.filter((c: any) => {
        const step = (c.crm_workflow_steps || []).find((s: any) => s.step_name === 'Booking');
        return step && step.status === 'Done';
      }).length;
      setBookingCount(count);
      return updated;
    });

    try {
      const isUuid = (str: any) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
      const validUserId = isUuid(userId) ? userId : null;
      const res = await apiService.updateCrmWorkflowStep(stepId, { 
        status: modalStatus, 
        reason: modalStatus === 'Done' ? null : modalReason || undefined,
        updated_by: validUserId 
      });
      if (!res || !res.success) {
        alert(res?.message || 'Update failed on server');
        fetchData();
      } else {
        fetchData();
      }
    } catch (err: any) {
      console.error('Workflow step update error:', err);
      alert('Failed to update step: ' + (err.message || 'Server error'));
      fetchData();
    } finally {
      setSaving(null);
      setActiveUpdateModal(null);
    }
  };

  const handleSaveCarModal = async () => {
    if (!editingCarModal) return;
    setSavingCar(true);
    try {
      const res = await apiService.updateCrmCustomer(editingCarModal.id, {
        car_details: selectedCarDetails,
        performed_by: userId
      });
      if (res.success) {
        setCustomers(prev => prev.map(c => c.id === editingCarModal.id ? { ...c, car_details: selectedCarDetails } : c));
        setEditingCarModal(null);
      } else {
        alert(res.message || 'Failed to update vehicle details');
      }
    } catch (err: any) {
      alert('Error saving vehicle details: ' + (err.message || 'Error'));
    } finally {
      setSavingCar(false);
    }
  };

  // Helper to find the first step that is not Done for a customer
  const getCustomerCurrentStep = (c: any) => {
    const steps = c.crm_workflow_steps || [];
    for (const stepName of WORKFLOW_STEPS) {
      const step = steps.find((s: any) => s.step_name === stepName);
      if (step && step.status !== 'Done') {
        return step;
      }
    }
    // If all are Done, return the last step
    const lastStep = steps.find((s: any) => s.step_name === WORKFLOW_STEPS[WORKFLOW_STEPS.length - 1]);
    return lastStep || null;
  };

  const stepSummary = WORKFLOW_STEPS.map(step => {
    const activeCustomersForStep = customers.filter(c => {
      const currentStep = getCustomerCurrentStep(c);
      return currentStep && currentStep.step_name === step;
    });

    return {
      step,
      total:   activeCustomersForStep.length,
      pending: activeCustomersForStep.filter(c => {
        const cs = getCustomerCurrentStep(c);
        return cs && cs.status === 'Pending';
      }).length,
      hold:    activeCustomersForStep.filter(c => {
        const cs = getCustomerCurrentStep(c);
        return cs && cs.status === 'Hold';
      }).length,
      done:    activeCustomersForStep.filter(c => {
        const cs = getCustomerCurrentStep(c);
        return cs && cs.status === 'Done';
      }).length,
    };
  });

  const totalPending = stepSummary.reduce((a, b) => a + b.pending, 0);
  const totalHold    = stepSummary.reduce((a, b) => a + b.hold, 0);
  const totalDone    = stepSummary.reduce((a, b) => a + b.done, 0);

  const filteredCustomers = customers.filter(c => {
    if (activeStep) {
      const currentStep = getCustomerCurrentStep(c);
      if (!currentStep || currentStep.step_name !== activeStep) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!c.customer_name?.toLowerCase().includes(q) && !c.phone?.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="amv-loading-wrapper">
        <div className="amv-loading-card">
          <div className="amv-spinner" />
          <div>
            <div className="amv-loading-title">Loading Admin Master View</div>
            <div className="amv-loading-sub">Fetching customer pipeline data…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="amv-loading-wrapper">
        <div className="amv-error-card">
          <div>
            <div className="amv-loading-title">{error}</div>
            <button className="amv-btn amv-btn-primary" style={{ marginTop: 12 }} onClick={() => fetchData(false)}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="amv-root">
      {selectedJourneyId && (
        <CustomerJourneyModal
          customerId={selectedJourneyId}
          onClose={() => setSelectedJourneyId(null)}
        />
      )}

      {/* Page Header */}
      <div className="amv-header">
        <div className="amv-header-left">
          <div>
            <h2 className="amv-header-title">Admin Master View</h2>
            <p className="amv-header-sub">
              {customers.length} customers&nbsp;·&nbsp;
              <span className="amv-stat-inline amv-pending">{totalPending} Pending</span>&nbsp;·&nbsp;
              <span className="amv-stat-inline amv-hold">{totalHold} Hold</span>&nbsp;·&nbsp;
              <span className="amv-stat-inline amv-done">{totalDone} Done</span>&nbsp;·&nbsp;
              <span className="amv-stat-inline amv-bookings">{bookingCount} Bookings</span>
            </p>
          </div>
        </div>
        <div className="amv-header-actions">
          <button className="amv-btn amv-btn-ghost" onClick={() => fetchData(false)}>Refresh</button>
        </div>
      </div>

      {/* SLA Alert */}
      <SLAAlertBanner customers={customers} slaDays={3} />

      {/* Pipeline Summary Cards */}
      <div className="amv-pipeline-grid">
        {stepSummary.map(s => {
          const isActive = activeStep === s.step;
          const pct      = s.total ? Math.round((s.done / s.total) * 100) : 0;
          const hasIssue = s.hold > 0;
          return (
            <div
              key={s.step}
              className={`amv-pipeline-card ${isActive ? 'amv-pipeline-card--active' : ''} ${hasIssue ? 'amv-pipeline-card--issue' : ''}`}
              onClick={() => setActiveStep(isActive ? null : s.step)}
              title={`Click to filter by ${s.step}`}
            >
              <div className="amv-pipeline-card-top">
                <span className="amv-pipeline-label">{s.step}</span>
              </div>
              <div className="amv-pipeline-counts">
                {s.pending > 0 && (
                  <span className="amv-badge amv-badge--pending">{s.pending} Pending</span>
                )}
                {s.hold > 0 && (
                  <span className="amv-badge amv-badge--hold">{s.hold} Hold</span>
                )}
                {s.done > 0 && (
                  <span className="amv-badge amv-badge--done">{s.done} Done</span>
                )}
                {s.total === 0 && (
                  <span className="amv-badge amv-badge--empty">No data</span>
                )}
              </div>
              <div className="amv-progress-bar-track">
                <div className="amv-progress-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="amv-pipeline-pct">{pct}% complete</div>
            </div>
          );
        })}
      </div>

      {/* Search & Filter Bar */}
      <div className="amv-toolbar">
        <div className="amv-search-wrap">
          <svg className="amv-search-icon-svg" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M14 14l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            className="amv-search-input"
            placeholder="Search by name or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="amv-search-clear" onClick={() => setSearch('')}>×</button>
          )}
        </div>

        <div className="amv-toolbar-right">
          {activeStep && (
            <div className="amv-filter-chip">
              <span>Filter: <strong>{activeStep}</strong></span>
              <button className="amv-filter-chip-close" onClick={() => setActiveStep(null)}>×</button>
            </div>
          )}
          <span className="amv-result-count">{filteredCustomers.length} result{filteredCustomers.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Main Table or Empty State */}
      {filteredCustomers.length === 0 ? (
        <div className="amv-empty">
          <div className="amv-empty-title">
            {search
              ? `No results for "${search}"`
              : activeStep
              ? `No active customers at "${activeStep}"`
              : 'No customers yet'}
          </div>
          <div className="amv-empty-sub">
            {search
              ? 'Try a different name or phone number.'
              : activeStep
              ? 'All customers have completed this stage.'
              : 'Convert a lead to get started.'}
          </div>
          {(search || activeStep) && (
            <button
              className="amv-btn amv-btn-ghost"
              style={{ marginTop: 12 }}
              onClick={() => { setSearch(''); setActiveStep(null); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="amv-table-card">
          <div className="amv-table-scroll">
            <table className="amv-table">
              <thead>
                <tr>
                  <th className="amv-th amv-th--customer">Customer</th>
                  <th className="amv-th">Phone</th>
                  <th className="amv-th">Car Details</th>
                  {activeStep ? (
                    <>
                      <th className="amv-th">{activeStep} Status</th>
                      <th className="amv-th">Quick Update</th>
                    </>
                  ) : (
                    WORKFLOW_STEPS.map(step => (
                      <th
                        key={step}
                        className="amv-th amv-th--step"
                        onClick={() => setActiveStep(step)}
                        title={`Filter by ${step}`}
                      >
                        {step}
                        <span className="amv-th-filter-hint">▼</span>
                      </th>
                    ))
                  )}
                  {!activeStep && <th className="amv-th">Timeline</th>}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((c: any) => {
                  const stepMap: Record<string, any> = {};
                  (c.crm_workflow_steps || []).forEach((s: any) => { stepMap[s.step_name] = s; });

                  const overallDone = WORKFLOW_STEPS.filter(st => stepMap[st]?.status === 'Done').length;
                  const overallPct  = Math.round((overallDone / WORKFLOW_STEPS.length) * 100);

                  const getDaysAtCurrentStep = (customer: any) => {
                    const steps = customer.crm_workflow_steps || customer.workflow_steps || [];
                    if (steps.length === 0) return { days: 0, stepName: null };
                    const currentStepIndex = steps.findIndex((s: any) => s.status !== 'Done');
                    if (currentStepIndex === -1) return { days: 0, stepName: null };
                    const currentStep = steps[currentStepIndex];
                    let arrivalTime = new Date(customer.created_at).getTime();
                    if (currentStepIndex > 0) {
                      const prevStep = steps[currentStepIndex - 1];
                      if (prevStep.updated_at) arrivalTime = new Date(prevStep.updated_at).getTime();
                    }
                    const days = Math.floor((Date.now() - arrivalTime) / (1000 * 60 * 60 * 24));
                    return { days, stepName: currentStep.step_name };
                  };

                  const slaInfo = getDaysAtCurrentStep(c);
                  const isSlaBreached = slaInfo.days >= 3 && slaInfo.stepName;

                  return (
                    <tr key={c.id} className="amv-tr">
                      {/* Customer */}
                      <td className="amv-td amv-td--customer">
                        <div className="amv-customer-cell">
                          <div className="amv-avatar">
                            {(c.customer_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div
                              className="amv-customer-name"
                              onClick={() => setSelectedJourneyId(c.id)}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}
                            >
                              {c.customer_name}
                              {isSlaBreached && (
                                <span 
                                  style={{ 
                                    background: '#FEE2E2', 
                                    color: '#DC2626', 
                                    fontSize: '10px', 
                                    padding: '1px 5px', 
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    border: '1px solid #FCA5A5',
                                    display: 'inline-flex',
                                    alignItems: 'center'
                                  }}
                                  title={`Stuck at ${slaInfo.stepName} for ${slaInfo.days} days`}
                                >
                                  ⚠️ stuck {slaInfo.days}d
                                </span>
                              )}
                            </div>
                            <div className="amv-customer-id">#{c.id.slice(0, 8)}</div>
                            {!activeStep && (
                              <div className="amv-mini-progress">
                                <div className="amv-mini-progress-fill" style={{ width: `${overallPct}%` }} />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Phone */}
                      <td className="amv-td">
                        <span className="amv-phone">{c.phone || '—'}</span>
                      </td>

                      {/* Car Details */}
                      <td className="amv-td amv-td--car">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '13px' }}>
                            {c.car_details || <span className="amv-muted" style={{ fontStyle: 'italic', color: '#94A3B8' }}>Not assigned</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCarDetails(c.car_details || '');
                              setEditingCarModal({
                                id: c.id,
                                customerName: c.customer_name,
                                phone: c.phone,
                                currentCar: c.car_details || ''
                              });
                            }}
                            style={{
                              background: '#F1F5F9',
                              border: '1px solid #CBD5E1',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              color: '#475569',
                              padding: '2px 6px',
                              fontWeight: 600,
                              lineHeight: 1,
                              whiteSpace: 'nowrap'
                            }}
                            title="Assign or Edit Car Details"
                          >
                            ✏️
                          </button>
                        </div>
                      </td>

                      {/* Active Step Mode */}
                      {activeStep ? (
                        <>
                          <td className="amv-td">
                            {stepMap[activeStep] ? (
                              <div className="amv-step-status-cell">
                                <span
                                  className="amv-status-pill"
                                  style={{
                                    background: STATUS_CONFIG[stepMap[activeStep].status]?.bg,
                                    color: STATUS_CONFIG[stepMap[activeStep].status]?.text,
                                    border: `1px solid ${STATUS_CONFIG[stepMap[activeStep].status]?.border}`,
                                  }}
                                >
                                  {stepMap[activeStep].status}
                                </span>
                                {stepMap[activeStep].reason && (
                                  <div className="amv-step-reason">{stepMap[activeStep].reason}</div>
                                )}
                                {stepMap[activeStep].updated_by?.name && (
                                  <div className="amv-step-updater">by {stepMap[activeStep].updated_by.name}</div>
                                )}
                              </div>
                            ) : <span className="amv-muted">—</span>}
                          </td>
                          <td className="amv-td">
                            {stepMap[activeStep] && (
                              <div className="amv-quick-actions">
                                {['Done', 'Hold', 'Pending'].map(s => (
                                  <button
                                    key={s}
                                    disabled={saving === stepMap[activeStep].id}
                                    className={`amv-quick-btn amv-quick-btn--${s.toLowerCase()} ${stepMap[activeStep].status === s ? 'amv-quick-btn--active' : ''}`}
                                    onClick={() => openUpdateModal(stepMap[activeStep], c)}
                                    title={`Mark as ${s}`}
                                  >
                                    {s}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                        </>
                      ) : (
                        WORKFLOW_STEPS.map(step => {
                          const s = stepMap[step];
                          return (
                            <td key={step} className="amv-td amv-td--step-cell">
                              {s ? (
                                <span
                                  className="amv-dot-badge"
                                  style={{
                                    background: STATUS_CONFIG[s.status]?.bg,
                                    color: STATUS_CONFIG[s.status]?.text,
                                  }}
                                  title={s.status}
                                >
                                  {s.status.slice(0, 1)}
                                </span>
                              ) : (
                                <span className="amv-dot-badge amv-dot-badge--empty" title="Not started">·</span>
                              )}
                            </td>
                          );
                        })
                      )}

                      {/* Timeline Button */}
                      {!activeStep && (
                        <td className="amv-td">
                          <button
                            className="amv-timeline-btn"
                            onClick={() => setSelectedJourneyId(c.id)}
                            title="View customer journey timeline"
                          >
                            Timeline
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="amv-table-footer">
            <span>{filteredCustomers.length} of {customers.length} customers shown</span>
            {activeStep && (
              <button className="amv-btn amv-btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => setActiveStep(null)}>
                Clear filter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Team Presence & Workload Monitor */}
      <div className="amv-workload-panel">
        <h3 className="amv-workload-title">Team Presence &amp; Workload Monitor</h3>
        <div className="amv-workload-grid">
          {users.map((u: any) => {
            const isOnline = u.last_active_at ? (Date.now() - new Date(u.last_active_at).getTime()) < (5 * 60 * 1000) : false;
            let statusColor = '#94A3B8'; // Offline
            let statusLabel = 'Offline';

            if (isOnline) {
              const status = u.availability_status || 'Available';
              if (status === 'Available') {
                statusColor = '#10B981'; // Green
                statusLabel = 'Available';
              } else if (status === 'Busy') {
                statusColor = '#EF4444'; // Red
                statusLabel = 'Busy';
              } else if (status === 'Break') {
                statusColor = '#F59E0B'; // Orange
                statusLabel = 'On Break';
              } else {
                statusColor = '#10B981';
                statusLabel = 'Available';
              }
            }

            const activeLeads = u.workload?.leads || 0;
            const openTasks = u.workload?.tasks || 0;
            const totalWorkload = activeLeads + openTasks;

            return (
              <div key={u.id} className="amv-user-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span
                      style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: statusColor,
                        flexShrink: 0,
                        boxShadow: `0 0 4px ${statusColor}`
                      }}
                      title={`Status: ${statusLabel}`}
                    />
                    <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--crm-text-primary)' }}>{u.name}</span>
                  </div>
                  <span style={{ fontSize: '11px', background: '#F1F5F9', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                    {u.role}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px' }}>
                  <span style={{ color: 'var(--crm-text-secondary)' }}>Leads: <strong>{activeLeads}</strong></span>
                  <span style={{ color: 'var(--crm-text-secondary)' }}>Tasks: <strong>{openTasks}</strong></span>
                </div>
                <div style={{ marginTop: '6px', height: '3px', background: '#E2E8F0', borderRadius: '2px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      background: totalWorkload > 8 ? '#EF4444' : totalWorkload > 4 ? '#F59E0B' : '#10B981',
                      width: `${Math.min(100, (totalWorkload / 12) * 100)}%`,
                      borderRadius: '2px',
                      transition: 'width 0.4s'
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Proper Update Status Modal Popup */}
      {activeUpdateModal && (
        <div className="crm-modal-overlay" onClick={() => setActiveUpdateModal(null)}>
          <div className="crm-modal" style={{ width: '450px', padding: '24px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--crm-text-primary)' }}>
                🔄 Update Stage: {activeUpdateModal.stepName}
              </h3>
              <button 
                onClick={() => setActiveUpdateModal(null)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--crm-text-muted)', padding: 0 }}
              >
                ✕
              </button>
            </div>
            
            <p style={{ margin: '0 0 16px 0', fontSize: '13.5px', color: 'var(--crm-text-secondary)' }}>
              Updating workflow stage for <strong>{activeUpdateModal.customerName}</strong>
            </p>

            {/* Status selection buttons */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-text-secondary)', marginBottom: '8px' }}>
                Select Status
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                {['Pending', 'Hold', 'Done'].map(statusOption => {
                  const isActive = modalStatus === statusOption;
                  let btnBg = '#F8FAFC';
                  let btnColor = '#475569';
                  let btnBorder = '#E2E8F0';
                  
                  if (isActive) {
                    if (statusOption === 'Done') {
                      btnBg = '#D1FAE5'; btnColor = '#065F46'; btnBorder = '#10B981';
                    } else if (statusOption === 'Hold') {
                      btnBg = '#FEE2E2'; btnColor = '#991B1B'; btnBorder = '#EF4444';
                    } else {
                      btnBg = '#FEF3C7'; btnColor = '#92400E'; btnBorder = '#F59E0B';
                    }
                  }

                  return (
                    <button
                      key={statusOption}
                      type="button"
                      onClick={() => setModalStatus(statusOption)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        borderRadius: '6px',
                        fontWeight: 700,
                        fontSize: '13px',
                        cursor: 'pointer',
                        background: btnBg,
                        color: btnColor,
                        border: `1px solid ${btnBorder}`,
                        transition: 'all 0.15s ease',
                        transform: isActive ? 'scale(1.02)' : 'none',
                        boxShadow: isActive ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                      }}
                    >
                      {statusOption}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Reason/comments input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-text-secondary)', marginBottom: '8px' }}>
                Comments / Reason (Optional)
              </label>
              <textarea
                value={modalReason}
                onChange={e => setModalReason(e.target.value)}
                placeholder={
                  modalStatus === 'Hold'
                    ? 'State the reason for placing this step on Hold (e.g. documents missing)...'
                    : modalStatus === 'Pending'
                    ? 'State reasons why this step is pending...'
                    : 'Add notes for the completed stage...'
                }
                style={{
                  width: '100%',
                  height: '90px',
                  borderRadius: '6px',
                  border: '1px solid #CBD5E1',
                  padding: '10px',
                  fontSize: '13px',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setActiveUpdateModal(null)}
                className="amv-btn amv-btn-ghost"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalSubmit}
                className="amv-btn amv-btn-primary"
                style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 600, background: '#4F46E5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                disabled={saving === activeUpdateModal.stepId}
              >
                {saving === activeUpdateModal.stepId ? 'Saving...' : '✓ Update Stage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vehicle / Car Details Modal */}
      {editingCarModal && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditingCarModal(null);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '520px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #E2E8F0',
              overflow: 'hidden',
              animation: 'crm-fade-in 0.15s ease-out'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#F8FAFC'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
                  🚗 Assign / Edit Car Details
                </h3>
                <div style={{ fontSize: '12px', color: '#64748B', marginTop: '3px' }}>
                  Customer: <strong>{editingCarModal.customerName}</strong> ({editingCarModal.phone})
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingCarModal(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '18px',
                  color: '#94A3B8',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px' }}>
              <CarSelector 
                value={selectedCarDetails}
                onChange={(val: string) => setSelectedCarDetails(val)}
              />
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '14px 20px',
              background: '#F8FAFC',
              borderTop: '1px solid #E2E8F0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                type="button"
                onClick={() => setEditingCarModal(null)}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#64748B',
                  background: 'white',
                  border: '1px solid #CBD5E1',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveCarModal}
                disabled={savingCar}
                style={{
                  padding: '8px 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'white',
                  background: '#4F46E5',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: savingCar ? 'not-allowed' : 'pointer',
                  opacity: savingCar ? 0.7 : 1
                }}
              >
                {savingCar ? 'Saving...' : 'Save Car Details'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMasterDashboard;
